import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, doc, updateDoc, deleteDoc,
  addDoc, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, BookOpen, Upload,
  LogOut, Trash2, ShieldCheck,
  ShieldOff, Search, RefreshCw, X, Check,
  AlertTriangle, Plus, TrendingUp, Pencil, ArrowLeft, Eye,
} from "lucide-react";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";

// ── Helpers ────────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] text-[var(--muted-text)] font-medium uppercase tracking-wider">{label}</span>
        <Icon className="w-4 h-4 text-[var(--faint-text)]" />
      </div>
      <p className="text-2xl font-semibold text-[var(--text)] tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-[var(--subtle-text)] mt-1">{sub}</p>}
    </div>
  );
}

function Badge({ role }) {
  if (role === "admin")
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#60a5fa]/10 text-[#60a5fa] text-[10px] font-medium border border-[#60a5fa]/20">Admin</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--soft-fill)] text-[var(--subtle-text)] text-[10px] font-medium border border-[var(--border-ui)]">User</span>;
}

// ── User Growth Chart ──────────────────────────────────────────────────────────

function UserGrowthChart({ users }) {
  const data = React.useMemo(() => {
    if (!users || users.length === 0) return [];
    const buckets = {};
    users.forEach(u => {
      const ts = u.createdAt?.toDate ? u.createdAt.toDate() : u.createdAt ? new Date(u.createdAt) : null;
      if (!ts) return;
      const key = ts.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
      buckets[key] = (buckets[key] || 0) + 1;
    });
    const sorted = Object.entries(buckets).sort((a, b) => {
      const parse = (s) => { const [m, y] = s.split(" "); return new Date(`${m} 20${y}`); };
      return parse(a[0]) - parse(b[0]);
    });
    let cum = 0;
    return sorted.map(([month, count]) => { cum += count; return { month, users: cum }; });
  }, [users]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[var(--surface-elevated)] border border-[var(--border-ui)] rounded-lg px-3 py-2 shadow-xl">
        <p className="text-[10px] text-[var(--subtle-text)] mb-0.5">{label}</p>
        <p className="text-[13px] font-semibold text-[var(--text)]">{payload[0].value} users</p>
      </div>
    );
  };

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-[var(--subtle-text)] text-[13px]">
        No signup data yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={180}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
        <defs>
          <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#60a5fa" stopOpacity={0.18} />
            <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fill: "#414960", fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fill: "#414960", fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(255,255,255,0.06)" }} />
        <Area
          type="monotone"
          dataKey="users"
          stroke="#60a5fa"
          strokeWidth={1.5}
          fill="url(#userGrad)"
          dot={false}
          activeDot={{ r: 3, fill: "#60a5fa", strokeWidth: 0 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────

function OverviewTab({ stats, users }) {
  return (
    <div>
      <h2 className="text-[16px] font-semibold tracking-tight text-[var(--text)] mb-5">Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Users} label="Total Users" value={stats.users} sub="Registered accounts" />
        <StatCard icon={BookOpen} label="Questions" value={stats.questions} sub="Custom questions" />
        <StatCard icon={ShieldCheck} label="Admins" value={stats.admins} sub="Admin accounts" />
        <StatCard icon={TrendingUp} label="Papers" value={stats.papers} sub="Official paper sets" />
      </div>

      <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[13px] font-medium text-[var(--text)]">User Growth</h3>
            <p className="text-[12px] text-[var(--subtle-text)] mt-0.5">Cumulative signups over time</p>
          </div>
          <span className="text-[12px] text-[var(--subtle-text)]">{typeof stats.users === "number" ? stats.users : "—"} total</span>
        </div>
        <UserGrowthChart users={users} />
      </div>
    </div>
  );
}

// ── Users Tab ──────────────────────────────────────────────────────────────────

function UsersTab() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [toast, setToast] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "users"), orderBy("createdAt", "desc")));
    setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const toggleRole = async (u) => {
    if (u.uid === currentUser.uid) return showToast("Cannot change your own role.", "error");
    setUpdating(u.id);
    const newRole = u.role === "admin" ? "user" : "admin";
    await updateDoc(doc(db, "users", u.id), { role: newRole });
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, role: newRole } : x));
    showToast(`${u.displayName || u.email} is now ${newRole}.`);
    setUpdating(null);
  };

  const removeUser = async (u) => {
    if (u.uid === currentUser.uid) return showToast("Cannot remove yourself.", "error");
    if (!window.confirm(`Remove ${u.email} from the platform?`)) return;
    await deleteDoc(doc(db, "users", u.id));
    setUsers(prev => prev.filter(x => x.id !== u.id));
    showToast(`${u.email} removed.`);
  };

  const filtered = users.filter(u =>
    (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.displayName || "").toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-medium shadow-xl border ${
          toast.type === "error"
            ? "bg-[#1a0a0a] border-red-500/20 text-red-400"
            : "bg-[#0a1a0a] border-green-500/20 text-green-400"
        }`} style={{ animation: "scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)" }}>
          {toast.type === "error" ? <AlertTriangle className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[16px] font-semibold tracking-tight text-[var(--text)]">Users <span className="text-[var(--subtle-text)] font-normal ml-1">({users.length})</span></h2>
        <div className="flex items-center gap-2">
          <button onClick={fetchUsers} className="p-1.5 text-[var(--subtle-text)] hover:text-[var(--text)] transition-colors rounded-md hover:bg-[var(--soft-fill)]">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-b from-[#60a5fa] to-[#2563eb] hover:brightness-110 text-white text-[11px] font-medium rounded-lg shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] transition-colors"
          >
            <Plus className="w-3 h-3" /> Add User
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--subtle-text)]" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-9 pr-4 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] placeholder-[var(--faint-text)] outline-none focus:border-[var(--border-ui)] transition-colors"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--subtle-text)] hover:text-[var(--text)]">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[var(--subtle-text)] text-[13px]">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-[var(--subtle-text)] text-[13px]">No users found.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-ui)]">
                <th className="text-left px-5 py-3.5 text-[11px] text-[var(--subtle-text)] font-medium uppercase tracking-wider">User</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[var(--subtle-text)] font-medium uppercase tracking-wider hidden sm:table-cell">Joined</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[var(--subtle-text)] font-medium uppercase tracking-wider">Role</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-ui)]">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-[var(--soft-fill)] transition-colors group">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      {u.photoURL ? (
                        <img src={u.photoURL} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-[var(--surface-elevated)] border border-[var(--border-ui)] flex items-center justify-center text-[10px] text-[var(--subtle-text)]">
                          {(u.displayName || u.email || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-[13px] text-[var(--text)]">{u.displayName || "—"}</p>
                        <p className="text-[12px] text-[var(--subtle-text)]">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <span className="text-[12px] text-[var(--subtle-text)]">{formatDate(u.createdAt)}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <Badge role={u.role} />
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => toggleRole(u)}
                        disabled={updating === u.id || u.uid === currentUser.uid}
                        title={u.role === "admin" ? "Remove admin" : "Make admin"}
                        className="p-1.5 rounded-md text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)] transition-all disabled:opacity-30"
                      >
                        {u.role === "admin" ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => removeUser(u)}
                        disabled={u.uid === currentUser.uid}
                        title="Remove user"
                        className="p-1.5 rounded-md text-[var(--subtle-text)] hover:text-red-400 hover:bg-red-500/[0.08] transition-all disabled:opacity-30"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && <AddUserModal onClose={() => setShowAddModal(false)} onAdded={fetchUsers} showToast={showToast} />}
    </div>
  );
}

function AddUserModal({ onClose, onAdded, showToast }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!email.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "invited_users"), {
        email: email.trim().toLowerCase(),
        role,
        invitedAt: serverTimestamp(),
        status: "pending",
      });
      showToast(`Invite sent for ${email}.`);
      onAdded();
      onClose();
    } catch (err) {
      showToast("Failed to add user.", "error");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-[var(--overlay)] backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl w-full max-w-sm p-6 shadow-2xl" style={{ animation: "scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)" }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[13px] font-medium text-[var(--text)]">Add User</h3>
          <button onClick={onClose} className="text-[var(--subtle-text)] hover:text-[var(--text)] transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[12px] text-[var(--muted-text)] mb-5">
          Pre-authorise an email. When this person signs in with Google, they'll automatically get the assigned role.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Email address</label>
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] placeholder-[var(--faint-text)] outline-none focus:border-[var(--border-ui)] transition-colors"
            />
          </div>
          <div>
            <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] outline-none focus:border-[var(--border-ui)] transition-colors"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button
            onClick={handleAdd}
            disabled={saving || !email.trim()}
            className="flex-1 py-2 bg-gradient-to-b from-[#60a5fa] to-[#2563eb] hover:brightness-110 disabled:opacity-40 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] text-[12px] font-medium rounded-lg transition-colors"
          >
            {saving ? "Adding…" : "Add User"}
          </button>
          <button onClick={onClose} className="flex-1 py-2 text-[var(--subtle-text)] hover:text-[var(--text)] text-[12px] rounded-lg border border-[var(--border-ui)] hover:border-[var(--border-ui)] transition-all">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Question Modal ────────────────────────────────────────────────────────

function EditQuestionModal({ question: q, onClose, onSaved, showToast }) {
  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const [questionText, setQuestionText] = useState(q.question || "");
  const [options, setOptions] = useState(q.options ? [...q.options] : [""]);
  const [correct, setCorrect] = useState(String(q.correct ?? 0));
  const [topic, setTopic] = useState(q.topic || "Algebra");
  const [year, setYear] = useState(String(q.year || ""));
  const [paper, setPaper] = useState(String(q.paper || "1"));
  const [questionNumber, setQuestionNumber] = useState(String(q.questionNumber || ""));
  const [questionType, setQuestionType] = useState(q.questionType || "official");
  const [diagramUrl, setDiagramUrl] = useState(q.diagramUrl || "");
  const [diagramScale, setDiagramScale] = useState(q.diagramScale ?? 1);
  const [communityProvider, setCommunityProvider] = useState(q.communityProvider || "");
  const [communityPaper, setCommunityPaper] = useState(String(q.communityPaper || ""));
  const [saving, setSaving] = useState(false);
  const [savingScale, setSavingScale] = useState(false);

  const topics = ["Algebra", "Calculus", "Geometry", "Probability", "Sequences", "Functions", "Logic", "Number Theory", "Trigonometry"];
  const inputCls = "w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] placeholder-[var(--faint-text)] outline-none focus:border-[var(--border-ui)] transition-colors";

  const saveScale = async () => {
    setSavingScale(true);
    try {
      await updateDoc(doc(db, "questions", q.id), { diagramScale });
      onSaved({ ...q, diagramScale });
      showToast("Scale saved.");
    } catch {
      showToast("Failed to save scale.", "error");
    }
    setSavingScale(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validOptions = options.filter(o => o.trim() !== "");
      const updateData = {
        questionNumber: parseInt(questionNumber),
        topic,
        question: questionText,
        options: validOptions,
        correct: parseInt(correct),
        year: parseInt(year),
        paper: ["1", "2"].includes(paper) ? parseInt(paper) : paper,
        questionType,
        diagramUrl: diagramUrl.trim(),
        diagramScale,
      };
      if (questionType === "community") {
        updateData.communityProvider = communityProvider.trim();
        updateData.communityPaper = communityPaper ? parseInt(communityPaper) : null;
      }
      await updateDoc(doc(db, "questions", q.id), updateData);
      showToast("Question updated.");
      onSaved({ ...q, ...updateData });
      onClose();
    } catch (err) {
      showToast("Failed to save.", "error");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-[var(--overlay)] backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl" style={{ animation: "scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-ui)] shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-[13px] font-medium text-[var(--text)]">
              Q{q.questionNumber} · {q.year} · {q.paper === "section-a" ? "Section A" : `P${q.paper}`}
            </h3>
            <div className="flex items-center gap-1 bg-[var(--soft-fill)] rounded-lg p-0.5">
              <button
                onClick={() => setMode("edit")}
                className={`px-3 py-1 rounded-md text-[11px] font-medium transition-all ${mode === "edit" ? "bg-[var(--soft-fill)] text-[var(--text)]" : "text-[var(--subtle-text)] hover:text-[var(--text)]"}`}
              >Edit</button>
              <button
                onClick={() => setMode("preview")}
                className={`px-3 py-1 rounded-md text-[11px] font-medium transition-all flex items-center gap-1.5 ${mode === "preview" ? "bg-[var(--soft-fill)] text-[var(--text)]" : "text-[var(--subtle-text)] hover:text-[var(--text)]"}`}
              ><Eye className="w-3 h-3" />Preview</button>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--subtle-text)] hover:text-[var(--text)] transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {/* Preview Body */}
        {mode === "preview" && (
          <div className="overflow-y-auto flex-1">
            <MathJaxContext>
              {/* Classic exam header bar */}
              <div style={{ backgroundColor: "rgb(0,109,170)", height: "38px", display: "flex", alignItems: "center", paddingLeft: "16px" }}>
                <span style={{ color: "white", fontFamily: "Tahoma, sans-serif", fontSize: "13pt" }}>Mathematics Admissions Test</span>
              </div>
              {/* Question content in classic white style */}
              <div style={{ backgroundColor: "white", padding: "24px 28px", fontFamily: "Tahoma, sans-serif", color: "black", fontSize: "12pt", lineHeight: "1.6" }}>
                <MathJax dynamic>
                  <div style={{ whiteSpace: "pre-wrap", marginBottom: "16px" }}>{questionText}</div>
                </MathJax>
                {diagramUrl && (
                  <div style={{ marginTop: "16px", marginBottom: "8px" }}>
                    <img
                      src={diagramUrl}
                      alt="Question diagram"
                      style={{ maxWidth: "100%", maxHeight: `${360 * diagramScale}px`, border: "1px solid #ced5e4" }}
                    />
                  </div>
                )}
                {diagramUrl && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px", marginBottom: "16px", padding: "10px 12px", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: "6px" }}>
                    <span style={{ fontSize: "10pt", color: "#636d87", whiteSpace: "nowrap" }}>Image scale</span>
                    <input
                      type="range" min={0.5} max={3} step={0.05} value={diagramScale}
                      onChange={e => setDiagramScale(parseFloat(e.target.value))}
                      style={{ flex: 1, accentColor: "rgb(0,109,170)" }}
                    />
                    <span style={{ fontSize: "10pt", fontFamily: "monospace", width: "38px", textAlign: "right" }}>{diagramScale.toFixed(2)}×</span>
                    <button
                      onClick={saveScale}
                      disabled={savingScale}
                      style={{ padding: "3px 10px", fontSize: "10pt", background: "rgb(0,109,170)", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", opacity: savingScale ? 0.5 : 1 }}
                    >{savingScale ? "Saving…" : "Save"}</button>
                  </div>
                )}
                <div style={{ marginTop: "16px" }}>
                  {options.map((opt, i) => (
                    <label key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px", cursor: "pointer" }}>
                      <input type="radio" name="preview-opt" readOnly checked={i === parseInt(correct)} style={{ accentColor: "rgb(0,109,170)" }} />
                      <MathJax dynamic><span>{opt}</span></MathJax>
                    </label>
                  ))}
                </div>
              </div>
            </MathJaxContext>
          </div>
        )}

        {/* Edit Body */}
        {mode === "edit" && <div className="overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Question #</label>
              <input value={questionNumber} onChange={e => setQuestionNumber(e.target.value)} type="number" className={inputCls} />
            </div>
            <div>
              <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Year</label>
              <input value={year} onChange={e => setYear(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Paper</label>
              <select value={paper} onChange={e => setPaper(e.target.value)} className={inputCls}>
                <option value="1">Paper 1</option>
                <option value="2">Paper 2</option>
                <option value="section-a">Section A (MAT)</option>
              </select>
            </div>
            <div>
              <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Type</label>
              <select value={questionType} onChange={e => setQuestionType(e.target.value)} className={inputCls}>
                <option value="official">Official</option>
                <option value="custom">Custom</option>
                <option value="community">Community</option>
                <option value="contingency">Contingency</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Topic</label>
              <select value={topic} onChange={e => setTopic(e.target.value)} className={inputCls}>
                {topics.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Correct answer (0 = A)</label>
              <input value={correct} onChange={e => setCorrect(e.target.value)} type="number" min="0" max="7" className={inputCls} />
            </div>
          </div>

          {questionType === "community" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-[#60a5fa]/[0.04] border border-[#60a5fa]/[0.12] rounded-lg">
              <div>
                <label className="block text-[12px] text-[#60a5fa]/70 mb-1.5">Community Provider</label>
                <input value={communityProvider} onChange={e => setCommunityProvider(e.target.value)} placeholder="e.g. Yotta" className={inputCls} />
              </div>
              <div>
                <label className="block text-[12px] text-[#60a5fa]/70 mb-1.5">Community Paper #</label>
                <input value={communityPaper} onChange={e => setCommunityPaper(e.target.value)} type="number" placeholder="1 or 2" className={inputCls} />
              </div>
            </div>
          )}

          <div>
            <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Diagram URL <span className="text-[var(--faint-text)]">(optional)</span></label>
            <input value={diagramUrl} onChange={e => setDiagramUrl(e.target.value)} placeholder="https://…" className={inputCls} />
          </div>

          <div>
            <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Question text (supports $LaTeX$)</label>
            <textarea value={questionText} onChange={e => setQuestionText(e.target.value)} rows={6} className={`${inputCls} resize-y font-mono text-[11px] leading-relaxed`} />
          </div>

          <div>
            <label className="block text-[12px] text-[var(--muted-text)] mb-1.5">Options</label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`text-[10px] w-5 text-center font-medium shrink-0 ${i === parseInt(correct) ? "text-[#60a5fa]" : "text-[var(--subtle-text)]"}`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <input
                    value={opt}
                    onChange={e => setOptions(prev => prev.map((o, j) => j === i ? e.target.value : o))}
                    className={inputCls}
                  />
                  <button onClick={() => setOptions(prev => prev.filter((_, j) => j !== i))} className="text-[var(--subtle-text)] hover:text-red-400 transition-colors shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => setOptions(prev => [...prev, ""])} className="flex items-center gap-1.5 text-[11px] text-[var(--subtle-text)] hover:text-[var(--text)] transition-colors mt-1">
                <Plus className="w-3 h-3" /> Add option
              </button>
            </div>
          </div>
        </div>}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--border-ui)] flex gap-2 shrink-0">
          {mode === "edit" && <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-gradient-to-b from-[#60a5fa] to-[#2563eb] hover:brightness-110 disabled:opacity-40 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] text-[12px] font-medium rounded-lg transition-colors">
            {saving ? "Saving…" : "Save Changes"}
          </button>}
          <button onClick={onClose} className="px-4 py-2 text-[var(--subtle-text)] hover:text-[var(--text)] text-[12px] rounded-lg border border-[var(--border-ui)] hover:border-[var(--border-ui)] transition-all">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Questions Tab ──────────────────────────────────────────────────────────────

function QuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTopic, setFilterTopic] = useState("All");
  const [filterYear, setFilterYear] = useState("All");
  const [filterPaper, setFilterPaper] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [filterProvider, setFilterProvider] = useState("");
  const [filterDiagram, setFilterDiagram] = useState("All");
  const [filterQNum, setFilterQNum] = useState("");
  const [toast, setToast] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const topics = ["All", "Algebra", "Calculus", "Geometry", "Probability", "Sequences", "Functions", "Logic", "Number Theory", "Trigonometry"];
  const years = ["All", ...Array.from({ length: 31 }, (_, i) => String(2026 - i))];
  const types = ["All", "official", "custom", "community", "contingency"];

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchQuestions = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "questions"));
    setQuestions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { fetchQuestions(); }, []);

  const deleteQuestion = async (q) => {
    if (!window.confirm(`Delete Q${q.questionNumber} (${q.year} P${q.paper})?`)) return;
    await deleteDoc(doc(db, "questions", q.id));
    setQuestions(prev => prev.filter(x => x.id !== q.id));
    showToast("Question deleted.");
  };

  const filtered = questions.filter(q => {
    const matchTopic    = filterTopic === "All" || q.topic === filterTopic;
    const matchYear     = filterYear === "All" || String(q.year) === filterYear;
    const matchPaper    = filterPaper === "All" || String(q.paper) === filterPaper;
    const matchType     = filterType === "All" || q.questionType === filterType;
    const matchProvider = !filterProvider || (q.communityProvider || "").toLowerCase().includes(filterProvider.toLowerCase());
    const matchDiagram  = filterDiagram === "All" || (filterDiagram === "yes" ? !!q.diagramUrl : !q.diagramUrl);
    const matchQNum     = !filterQNum || String(q.questionNumber) === filterQNum;
    const matchSearch   = !search || (q.question || "").toLowerCase().includes(search.toLowerCase());
    return matchTopic && matchYear && matchPaper && matchType && matchProvider && matchDiagram && matchQNum && matchSearch;
  }).sort((a, b) => {
    const examA = typeof a.paper === "string" ? 0 : 1; // MAT (section-a) before TMUA (1/2)
    const examB = typeof b.paper === "string" ? 0 : 1;
    if (examA !== examB) return examA - examB;
    if ((a.year ?? 0) !== (b.year ?? 0)) return (a.year ?? 0) - (b.year ?? 0);
    if ((a.paper ?? 0) !== (b.paper ?? 0)) return String(a.paper).localeCompare(String(b.paper));
    return (a.questionNumber ?? 0) - (b.questionNumber ?? 0);
  });

  const byType = questions.reduce((acc, q) => {
    const key = q.questionType || "unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-medium shadow-xl border ${
          toast.type === "error" ? "bg-[#1a0a0a] border-red-500/20 text-red-400" : "bg-[#0a1a0a] border-green-500/20 text-green-400"
        }`} style={{ animation: "scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)" }}>
          {toast.type === "error" ? <AlertTriangle className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[16px] font-semibold tracking-tight text-[var(--text)]">Questions <span className="text-[var(--subtle-text)] font-normal ml-1">({filtered.length} of {questions.length})</span></h2>
        <button onClick={fetchQuestions} className="p-1.5 text-[var(--subtle-text)] hover:text-[var(--text)] transition-colors rounded-md hover:bg-[var(--soft-fill)]">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Breakdown by type */}
      <div className="flex flex-wrap gap-2 mb-5">
        {["official", "community", "custom", "contingency", "unknown"].filter(t => t !== "unknown" || byType["unknown"]).map(type => (
          <div key={type} className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg px-3 py-2 flex items-center gap-3">
            <span className="text-[12px] text-[var(--muted-text)] capitalize">{type}</span>
            <span className="text-[12px] font-semibold text-[var(--text)]">{byType[type] || 0}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-2 mb-4">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[160px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--subtle-text)]" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search question text…"
              className="w-full pl-9 pr-4 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] placeholder-[var(--faint-text)] outline-none focus:border-[var(--border-ui)] transition-colors"
            />
          </div>
          <input
            value={filterQNum}
            onChange={e => setFilterQNum(e.target.value)}
            placeholder="Q#"
            type="number"
            className="px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] placeholder-[var(--faint-text)] outline-none focus:border-[var(--border-ui)] transition-colors w-20"
          />
          <input
            value={filterProvider}
            onChange={e => setFilterProvider(e.target.value)}
            placeholder="Community provider…"
            className="px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] placeholder-[var(--faint-text)] outline-none focus:border-[var(--border-ui)] transition-colors min-w-[150px]"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] outline-none">
            {types.map(t => <option key={t} value={t}>{t === "All" ? "All types" : t}</option>)}
          </select>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)} className="px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] outline-none">
            {years.map(y => <option key={y} value={y}>{y === "All" ? "All years" : y}</option>)}
          </select>
          <select value={filterPaper} onChange={e => setFilterPaper(e.target.value)} className="px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] outline-none">
            <option value="All">All papers</option>
            <option value="1">Paper 1</option>
            <option value="2">Paper 2</option>
            <option value="section-a">Section A</option>
          </select>
          <select value={filterTopic} onChange={e => setFilterTopic(e.target.value)} className="px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] outline-none">
            {topics.map(t => <option key={t} value={t}>{t === "All" ? "All topics" : t}</option>)}
          </select>
          <select value={filterDiagram} onChange={e => setFilterDiagram(e.target.value)} className="px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] outline-none">
            <option value="All">Diagram: any</option>
            <option value="yes">Has diagram</option>
            <option value="no">No diagram</option>
          </select>
          {(search || filterTopic !== "All" || filterYear !== "All" || filterPaper !== "All" || filterType !== "All" || filterProvider || filterDiagram !== "All" || filterQNum) && (
            <button onClick={() => { setSearch(""); setFilterTopic("All"); setFilterYear("All"); setFilterPaper("All"); setFilterType("All"); setFilterProvider(""); setFilterDiagram("All"); setFilterQNum(""); }}
              className="px-3 py-2 text-[11px] text-[var(--subtle-text)] hover:text-[var(--text)] border border-[var(--border-ui)] rounded-lg transition-colors">
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[var(--subtle-text)] text-[13px]">Loading questions…</div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-[var(--subtle-text)] text-[13px]">No questions match.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-ui)]">
                <th className="text-left px-5 py-3.5 text-[11px] text-[var(--subtle-text)] font-medium uppercase tracking-wider">Question</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[var(--subtle-text)] font-medium uppercase tracking-wider hidden sm:table-cell">Topic</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[var(--subtle-text)] font-medium uppercase tracking-wider hidden md:table-cell">Year / Paper</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[var(--subtle-text)] font-medium uppercase tracking-wider hidden lg:table-cell">Type</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-ui)]">
              {filtered.slice(0, 100).map(q => (
                <tr key={q.id} className="hover:bg-[var(--soft-fill)] transition-colors group">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] text-[var(--text)]">Q{q.questionNumber}</p>
                      {q.diagramUrl && <span className="text-[9px] text-[var(--subtle-text)] border border-[var(--border-ui)] rounded px-1 py-px">IMG</span>}
                    </div>
                    <p className="text-[12px] text-[var(--subtle-text)] mt-0.5 truncate max-w-[200px]">{(q.question || "").replace(/\$[^$]*\$/g, "[math]").slice(0, 60)}…</p>
                    {q.communityProvider && <p className="text-[10px] text-[#60a5fa]/60 mt-0.5">{q.communityProvider}{q.communityPaper ? ` P${q.communityPaper}` : ""}</p>}
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <span className="text-[12px] text-[var(--subtle-text)]">{q.topic || "—"}</span>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <span className="text-[12px] text-[var(--subtle-text)]">{q.year} · P{q.paper}</span>
                  </td>
                  <td className="px-5 py-3.5 hidden lg:table-cell">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                      q.questionType === "official"   ? "text-[var(--muted-text)] border-[var(--border-ui)] bg-[var(--soft-fill)]"
                    : q.questionType === "community"  ? "text-[#60a5fa] border-[#60a5fa]/20 bg-[#60a5fa]/[0.06]"
                    : q.questionType === "custom"     ? "text-emerald-400 border-emerald-500/20 bg-emerald-500/[0.06]"
                    : q.questionType === "practice"   ? "text-blue-400 border-blue-500/20 bg-blue-500/[0.06]"
                    : "text-[var(--subtle-text)] border-[var(--border-ui)]"
                    }`}>{q.questionType || "—"}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setEditingQuestion(q)}
                        className="p-1.5 rounded-md text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)] transition-all"
                        title="Edit question"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteQuestion(q)}
                        className="p-1.5 rounded-md text-[var(--subtle-text)] hover:text-red-400 hover:bg-red-500/[0.08] transition-all"
                        title="Delete question"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {filtered.length > 100 && (
          <div className="px-5 py-3 border-t border-[var(--border-ui)] text-[11px] text-[var(--subtle-text)]">
            Showing 100 of {filtered.length}. Use filters to narrow down.
          </div>
        )}
      </div>

      {editingQuestion && (
        <EditQuestionModal
          question={editingQuestion}
          onClose={() => setEditingQuestion(null)}
          onSaved={(updated) => {
            setQuestions(prev => prev.map(x => x.id === updated.id ? updated : x));
            setEditingQuestion(null);
          }}
          showToast={showToast}
        />
      )}

    </div>
  );
}

// ── Upload Tab ─────────────────────────────────────────────────────────────────

function UploadTab() {
  const [mode, setMode] = useState("manual"); // "manual" | "latex"
  const [latex, setLatex] = useState("");
  const [year, setYear] = useState("2024");
  const [paper, setPaper] = useState("1");
  const [questionNumber, setQuestionNumber] = useState("1");
  const [topic, setTopic] = useState("Algebra");
  const [questionType, setQuestionType] = useState("official");
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", "", "", "", "", ""]);
  const [correct, setCorrect] = useState("0");
  const [diagramUrl, setDiagramUrl] = useState("");
  const [communityProvider, setCommunityProvider] = useState("");
  const [communityPaper, setCommunityPaper] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const topics = ["Algebra", "Calculus", "Geometry", "Probability", "Sequences", "Functions", "Logic", "Number Theory", "Trigonometry"];

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const parseLatex = (text) => {
    const enumRegex = /\\begin\{enumerate\}([\s\S]*?)\\end\{enumerate\}/g;
    const allEnumerates = [...text.matchAll(enumRegex)];
    if (!allEnumerates.length) throw new Error("No \\begin{enumerate} block found.");
    const lastEnumerate = allEnumerates[allEnumerates.length - 1];
    const beforeOptions = text.slice(0, lastEnumerate.index).trim();
    const optionsContent = lastEnumerate[1];
    const itemRegex = /\\item\s*([\s\S]*?)(?=\\item|$)/g;
    const parsedOptions = [...optionsContent.matchAll(itemRegex)].map(m => m[1].trim().replace(/\n+/g, " "));
    return { questionText: beforeOptions, parsedOptions };
  };

  const handleLatexUpload = async () => {
    setSaving(true);
    try {
      const { questionText, parsedOptions } = parseLatex(latex);
      const latexDoc = {
        questionNumber: parseInt(questionNumber),
        topic,
        question: questionText,
        options: parsedOptions,
        correct: parseInt(correct),
        marks: 1,
        year: parseInt(year),
        paper: ["1", "2"].includes(paper) ? parseInt(paper) : paper,
        questionType,
        diagramUrl: diagramUrl.trim(),
        createdAt: serverTimestamp(),
      };
      if (questionType === "community") {
        latexDoc.communityProvider = communityProvider.trim();
        latexDoc.communityPaper = communityPaper ? parseInt(communityPaper) : null;
      }
      await addDoc(collection(db, "questions"), latexDoc);
      showToast(`Q${questionNumber} uploaded successfully.`);
      setLatex("");
    } catch (err) {
      showToast(err.message, "error");
    }
    setSaving(false);
  };

  const handleManualUpload = async () => {
    const validOptions = options.filter(o => o.trim());
    if (!question.trim() || validOptions.length < 2) return showToast("Fill in question and at least 2 options.", "error");
    setSaving(true);
    try {
      const manualDoc = {
        questionNumber: parseInt(questionNumber),
        topic,
        question: question.trim(),
        options: validOptions,
        correct: parseInt(correct),
        marks: 1,
        year: parseInt(year),
        paper: ["1", "2"].includes(paper) ? parseInt(paper) : paper,
        questionType,
        diagramUrl: diagramUrl.trim(),
        createdAt: serverTimestamp(),
      };
      if (questionType === "community") {
        manualDoc.communityProvider = communityProvider.trim();
        manualDoc.communityPaper = communityPaper ? parseInt(communityPaper) : null;
      }
      await addDoc(collection(db, "questions"), manualDoc);
      showToast(`Q${questionNumber} uploaded successfully.`);
      setQuestion("");
      setOptions(["", "", "", "", "", ""]);
    } catch (err) {
      showToast(err.message, "error");
    }
    setSaving(false);
  };

  const inputCls = "w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg text-[13px] text-[var(--text)] placeholder-[var(--faint-text)] outline-none focus:border-[var(--border-ui)] transition-colors";
  const labelCls = "block text-[12px] text-[var(--muted-text)] mb-1.5";

  return (
    <div>
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-medium shadow-xl border ${
          toast.type === "error" ? "bg-[#1a0a0a] border-red-500/20 text-red-400" : "bg-[#0a1a0a] border-green-500/20 text-green-400"
        }`} style={{ animation: "scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)" }}>
          {toast.type === "error" ? <AlertTriangle className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
          {toast.msg}
        </div>
      )}

      <h2 className="text-[16px] font-semibold tracking-tight text-[var(--text)] mb-5">Upload Question</h2>

      {/* Mode toggle */}
      <div className="flex gap-1 p-1 bg-[var(--surface)] border border-[var(--border-ui)] rounded-lg mb-6 w-fit">
        {["manual", "latex"].map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-4 py-1.5 rounded-md text-[12px] font-medium transition-all ${mode === m ? "bg-[#60a5fa] text-white shadow-[0_0_12px_rgba(96,165,250,0.3)]" : "text-[var(--subtle-text)] hover:text-[var(--text)]"}`}
          >
            {m === "manual" ? "Manual" : "LaTeX"}
          </button>
        ))}
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div>
          <label className={labelCls}>Year</label>
          <input value={year} onChange={e => setYear(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Paper</label>
          <select value={paper} onChange={e => setPaper(e.target.value)} className={inputCls}>
            <option value="1">Paper 1</option>
            <option value="2">Paper 2</option>
            <option value="section-a">Section A (MAT)</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Question #</label>
          <input value={questionNumber} onChange={e => setQuestionNumber(e.target.value)} type="number" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <select value={questionType} onChange={e => setQuestionType(e.target.value)} className={inputCls}>
            <option value="official">Official</option>
            <option value="custom">Custom</option>
            <option value="community">Community</option>
            <option value="contingency">Contingency</option>
          </select>
        </div>
      </div>

      {questionType === "community" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 p-3 bg-[#60a5fa]/[0.04] border border-[#60a5fa]/[0.12] rounded-lg">
          <div>
            <label className={labelCls + " !text-[#60a5fa]/70"}>Community Provider</label>
            <input value={communityProvider} onChange={e => setCommunityProvider(e.target.value)} placeholder="e.g. Yotta" className={inputCls} />
          </div>
          <div>
            <label className={labelCls + " !text-[#60a5fa]/70"}>Community Paper #</label>
            <input value={communityPaper} onChange={e => setCommunityPaper(e.target.value)} type="number" placeholder="1 or 2" className={inputCls} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <div>
          <label className={labelCls}>Topic</label>
          <select value={topic} onChange={e => setTopic(e.target.value)} className={inputCls}>
            {topics.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Correct answer (0 = A)</label>
          <input value={correct} onChange={e => setCorrect(e.target.value)} type="number" min="0" max="7" className={inputCls} />
        </div>
      </div>

      <div className="mb-5">
        <label className={labelCls}>Diagram URL <span className="text-[var(--faint-text)]">(optional)</span></label>
        <input value={diagramUrl} onChange={e => setDiagramUrl(e.target.value)} placeholder="https://…" className={inputCls} />
      </div>

      {mode === "latex" ? (
        <div className="mb-5">
          <label className={labelCls}>LaTeX source</label>
          <textarea
            value={latex}
            onChange={e => setLatex(e.target.value)}
            rows={10}
            placeholder={"Paste LaTeX question here…\nOptions must be in \\begin{enumerate}...\\end{enumerate}"}
            className={`${inputCls} font-mono resize-y`}
          />
        </div>
      ) : (
        <div className="space-y-3 mb-5">
          <div>
            <label className={labelCls}>Question text (supports $LaTeX$)</label>
            <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={4} className={`${inputCls} resize-y`} placeholder="Question text…" />
          </div>
          <div>
            <label className={labelCls}>Answer options</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--subtle-text)] w-4 text-right">{String.fromCharCode(65 + i)}</span>
                  <input
                    value={opt}
                    onChange={e => setOptions(prev => prev.map((o, j) => j === i ? e.target.value : o))}
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    className={inputCls}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={mode === "latex" ? handleLatexUpload : handleManualUpload}
        disabled={saving}
        className="px-5 py-2 bg-gradient-to-b from-[#60a5fa] to-[#2563eb] hover:brightness-110 disabled:opacity-40 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] text-[12px] font-medium rounded-lg transition-colors"
      >
        {saving ? "Uploading…" : "Upload Question"}
      </button>
    </div>
  );
}

// ── Main Admin Page ────────────────────────────────────────────────────────────

const NAV = [
  { id: "overview", label: "Overview",  icon: LayoutDashboard },
  { id: "users",    label: "Users",     icon: Users },
  { id: "questions",label: "Questions", icon: BookOpen },
  { id: "upload",   label: "Upload",    icon: Upload },
];

export default function AdminPage() {
  const { user, userDoc, loading, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({ users: "—", questions: "—", admins: "—", papers: "—" });
  const [rawUsers, setRawUsers] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    if (!loading && user && !isAdmin) navigate("/practice", { replace: true });
  }, [user, userDoc, loading, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      const [usersSnap, qDocs] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "questions")),
      ]);
      const allUsers = usersSnap.docs.map(d => d.data());
      setRawUsers(allUsers);
      const admins = allUsers.filter(u => u.role === "admin").length;
      const qData = qDocs.docs.map(d => d.data());
      const papers = new Set(qData.map(q => `${q.year}-${q.paper}`)).size;
      const customCount = qData.filter(q => q.questionType === "custom").length;
      setStats({
        users: allUsers.length,
        questions: customCount,
        admins,
        papers,
      });
    };
    load();
  }, [isAdmin]);

  if (loading || !isAdmin) return null;

  return (
    <div className="h-screen bg-[var(--bg)] text-[var(--text)] flex overflow-hidden">

        {/* ── Sidebar ── */}
        <aside className="w-[220px] shrink-0 border-r border-[var(--border-ui)] flex flex-col h-full bg-[var(--surface)]">
          <div className="px-5 py-4 border-b border-[var(--border-ui)]">
            <button onClick={() => navigate("/")} className="brand-btn" style={{ fontSize: "1rem" }}>
              tmua<span className="brand-prep">prep</span>
            </button>
            <p className="text-[10px] text-[var(--faint-text)] mt-0.5 uppercase tracking-widest font-medium">Admin</p>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            <button
              onClick={() => navigate("/practice")}
              className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm font-medium text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)] transition-all mb-2"
            >
              <ArrowLeft className="w-[17px] h-[17px] shrink-0" strokeWidth={1.5} />
              Practice Hub
            </button>
            <div className="h-px bg-[var(--soft-fill)] mb-2" />
            {NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  tab === id
                    ? "bg-[var(--soft-fill)] text-[var(--text)]"
                    : "text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)]"
                }`}
              >
                <Icon className="w-[17px] h-[17px] shrink-0" strokeWidth={1.5} />
                {label}
              </button>
            ))}
          </nav>

          {/* User */}
          <div className="border-t border-[var(--border-ui)] px-3 py-3 space-y-0.5">
            <button
              onClick={() => { signOut(); navigate("/"); }}
              className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-[var(--subtle-text)] hover:text-[var(--text)] hover:bg-[var(--soft-fill)] text-sm font-medium transition-all"
            >
              <LogOut className="w-[17px] h-[17px] shrink-0" strokeWidth={1.5} />
              Sign out
            </button>
            <div className="mt-1 pt-2 border-t border-[var(--border-ui)]">
              <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="" className="w-7 h-7 rounded-full shrink-0 ring-1 ring-white/[0.12]" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[var(--soft-fill)] border border-[var(--border-ui)] flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-medium text-[var(--text-soft)]">{(user.displayName || user.email || "?")[0].toUpperCase()}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-xs font-medium text-[var(--text)] truncate">{user.displayName || "Admin"}</div>
                  <div className="text-[10px] text-[var(--subtle-text)] truncate">{user.email}</div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main ── */}
        <main className="flex-1 min-w-0 overflow-auto px-10 py-8">
          {tab === "overview"  && <OverviewTab stats={stats} users={rawUsers} />}
          {tab === "users"     && <UsersTab />}
          {tab === "questions" && <QuestionsTab />}
          {tab === "upload"    && <UploadTab />}
        </main>

    </div>
  );
}
